import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import React, { cloneElement, useEffect, useRef, useState, type PropsWithChildren, type ReactNode } from 'react';
import { Button } from './Button';
import { classNames } from '~/utils/classNames';

export type DropdownItem = {
  label: ReactNode;
  value: string;
  type?: 'default' | 'danger';
};

export interface DropdownProps {
  trigger?: ReactNode;
  label?: string;
  contentClassName?: string;
  triggerClassName?: string;
  disabled?: boolean;
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export const Dropdown = ({
  label,
  trigger,
  contentClassName,
  triggerClassName,
  disabled,
  children,
  open,
  onOpenChange,
}: PropsWithChildren<DropdownProps>) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>(10);

  useEffect(() => {
    if (triggerRef.current) {
      setDropdownWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger disabled={disabled} className={triggerClassName} asChild>
        {trigger ? (
          cloneElement(trigger as React.ReactElement, { ref: triggerRef })
        ) : (
          <Button
            variant="secondary"
            size="sm"
            endIcon={open ? 'i-lucide:chevron-up' : 'i-lucide:chevron-down'}
            className="w-full"
          >
            {label}
          </Button>
        )}
      </DropdownMenu.Trigger>

      {open && (
        <DropdownMenu.Portal forceMount>
          <DropdownMenu.Content
            style={{ minWidth: dropdownWidth }}
            className={classNames(
              'bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 border-1 border-bolt-elements-borderColor rounded-md text-sm select-none flex flex-col p-1 gap-1 w-70 z-max',
              contentClassName,
            )}
            forceMount
            sideOffset={4}
            onFocusOutside={() => onOpenChange?.(false)}
          >
            {children}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      )}
    </DropdownMenu.Root>
  );
};

export interface DropdownItemProps {
  className?: string;
  onClick?: () => void;
}

Dropdown.Item = ({ className, onClick, children }: PropsWithChildren<DropdownItemProps>) => {
  return (
    <DropdownMenu.Item className="outline-none">
      <button
        className={classNames(
          'px-2 py-1.5 rounded-md bg-transparent enabled:hover:bg-bolt-ds-surfaceHighlight text-xs min-h-[30px] disabled:cursor-not-allowed disabled:opacity-50 w-full',
          className,
        )}
        onClick={onClick}
      >
        {children}
      </button>
    </DropdownMenu.Item>
  );
};
